import React, { useState, useEffect } from 'react';
import { X, Sparkles, Plus, Trash2, Loader2, Target, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';

// Use relative URL - works on ANY domain automatically!
const API = '/api';

const ComplexOfferModal = ({ isOpen, onClose, onSendOffer, caseData }) => {
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState(false);
  const [hoursMin, setHoursMin] = useState(5);
  const [hoursMax, setHoursMax] = useState(8);
  const [hourlyRate, setHourlyRate] = useState(1500);
  const [includes, setIncludes] = useState([
    'Grundig juridisk analyse',
    'Dokumentgjennomgang',
    'Juridisk memo med begrunnelse',
    'Anbefalte tiltak',
    'Oppfølging ved behov'
  ]);
  const [newIncludeItem, setNewIncludeItem] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && caseData) {
      getAIEstimate();
    }
  }, [isOpen, caseData]);

  const getAIEstimate = async () => {
    setIsEstimating(true);
    setEstimateError(false);
    
    console.log('🔍 Getting AI estimate for case:', {
      caseId: caseData?.id,
      category: caseData?.category,
      subcategory: caseData?.subcategory,
      hasDescription: !!(caseData?.description || caseData?.intake_summary)
    });
    
    try {
      const token = auth.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      if (!caseData?.id) {
        throw new Error('No case ID found');
      }
      
      const requestData = {
        category: caseData.category || 'Generell',
        subcategory: caseData.subcategory || '',
        description: caseData.description || caseData.intake_summary || 'Ingen beskrivelse',
        documents_count: caseData.uploaded_documents?.length || 0
      };
      
      console.log('📤 Sending AI estimate request:', {
        url: `${API}/marketplace/cases/${caseData.id}/assess-complex`,
        data: requestData
      });
      
      const response = await axios.post(
        `${API}/marketplace/cases/${caseData.id}/assess-complex`, 
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('✅ AI estimate received:', response.data);
      
      setHoursMin(response.data.hours_min || 5);
      setHoursMax(response.data.hours_max || 8);
      if (response.data.suggested_message) {
        setPersonalMessage(response.data.suggested_message);
      }
    } catch (error) {
      console.error('❌ AI estimate error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setEstimateError(true);
      // Set default message on error
      setPersonalMessage(`Hei,\n\nJeg har sett på din sak og den krever mer omfattende juridisk arbeid enn en standard sak.\n\nJeg estimerer ${hoursMin}-${hoursMax} timer fordi saken involverer ${caseData.category || 'komplekse juridiske spørsmål'} og krever grundig analyse.\n\nMed vennlig hilsen`);
    } finally {
      setIsEstimating(false);
    }
  };

  const addIncludeItem = () => {
    if (newIncludeItem.trim()) {
      setIncludes([...includes, newIncludeItem.trim()]);
      setNewIncludeItem('');
    }
  };

  const removeIncludeItem = (index) => {
    setIncludes(includes.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!personalMessage.trim()) {
      toast.error('Skriv en personlig melding til kunden');
      return;
    }

    setIsSubmitting(true);
    await onSendOffer({
      hours_min: hoursMin,
      hours_max: hoursMax,
      hourly_rate: hourlyRate,
      includes: includes,
      personal_message: personalMessage,
      total_min: hoursMin * hourlyRate,
      total_max: hoursMax * hourlyRate
    });
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  const totalMin = hoursMin * hourlyRate;
  const totalMax = hoursMax * hourlyRate;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Header - Linear Style */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Send tilbud til kunde</h2>
                <p className="text-gray-500 text-sm">{caseData?.title || 'Arbeidsrettslig tvist'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          
          {/* AI Estimation Loading */}
          {isEstimating && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <Sparkles className="w-8 h-8 text-gray-600 mx-auto mb-3 animate-pulse" />
              <p className="text-sm text-gray-700 font-medium">
                AI analyserer saken og estimerer arbeidstid...
              </p>
            </div>
          )}
          
          {/* AI Estimation Error */}
          {estimateError && !isEstimating && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">Kunne ikke få AI-estimat</p>
                <p className="text-sm text-amber-700 mt-1">
                  Vennligst juster verdiene manuelt basert på din vurdering av saken.
                </p>
              </div>
            </div>
          )}

          {/* Hours Estimation - Linear Style */}
          <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Estimert arbeidstid</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Minimum timer
                </label>
                <input
                  type="number"
                  value={hoursMin}
                  onChange={(e) => setHoursMin(Number(e.target.value))}
                  min="1"
                  max="50"
                  className="w-full border border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Maksimum timer
                </label>
                <input
                  type="number"
                  value={hoursMax}
                  onChange={(e) => setHoursMax(Number(e.target.value))}
                  min={hoursMin}
                  max="100"
                  className="w-full border border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none transition-all"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Timepris
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                  min="500"
                  max="5000"
                  step="100"
                  className="w-full border border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 rounded-md px-3 py-2 pr-20 text-sm focus:outline-none transition-all"
                />
                <span className="absolute right-3 top-1/2 -trangray-y-1/2 text-gray-500 text-xs font-medium">
                  kr/time
                </span>
              </div>
            </div>

            {/* Total Calculation - Linear Style */}
            <div className="mt-4 p-3 bg-white rounded-md border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Estimert totalpris:</span>
                <span className="text-lg font-semibold text-gray-900">
                  {totalMin.toLocaleString()} - {totalMax.toLocaleString()} kr
                </span>
              </div>
            </div>
          </div>

          {/* What's Included - Linear Style */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Dette inkluderer:</h3>
            <div className="space-y-1.5 mb-3">
              {includes.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-md group hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-sm">✓</span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                  <button
                    onClick={() => removeIncludeItem(index)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add new item */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newIncludeItem}
                onChange={(e) => setNewIncludeItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addIncludeItem()}
                placeholder="Legg til flere tjenester..."
                className="flex-1 border border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none transition-all"
              />
              <Button
                onClick={addIncludeItem}
                variant="outline"
                size="sm"
                className="px-3 h-9"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Personal Message - Linear Style */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">
              Personlig melding til kunde *
            </label>
            <p className="text-xs text-gray-500 mb-3">
              {estimateError ? 'Skriv en personlig melding til kunden' : 'AI har laget et forslag som du kan tilpasse'}
            </p>
            <textarea
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              className="w-full h-40 border border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 rounded-md px-3 py-2.5 text-sm focus:outline-none transition-all resize-none"
              placeholder="Hei [Kunde],

Jeg har gjennomgått din sak grundig, og den krever mer omfattende juridisk arbeid enn en standard sak.

Jeg estimerer 5-8 timer fordi..."
              required
            />
            <p className="text-xs text-gray-500 mt-1.5">
              {personalMessage.length} tegn · minimum 50 tegn
            </p>
          </div>
        </div>

        {/* Footer - Linear Style */}
        <div className="flex gap-2 p-4 bg-gray-50 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 h-9 text-sm"
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!personalMessage.trim() || personalMessage.length < 50 || isSubmitting}
            className="flex-1 h-9 text-sm bg-gray-900 hover:bg-gray-800 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sender...
              </>
            ) : (
              'Send tilbud til kunde'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComplexOfferModal;
